import { resolveShopifyUrl } from '@nx-paeonia/utils-shopify';
import { type } from 'arktype';
import localspace, { ttlPlugin } from 'localspace';
import { type ShallowRef, shallowRef } from 'vue';
import { type SearchProductItem, SearchProductItems } from '../domain/search-product.model.js';
import { useFetch } from './use.fetch.js';

const ttlOneHour = 3_600_000 as const;
const ttlTenMinutes = 60_000 as const;

const productsCache = localspace.createInstance({
  name: getCacheInstanceNameOnLocale('products'),
  plugins: [ttlPlugin({ defaultTTL: ttlOneHour })], // Auto-expire after 1h.
  coalesceWrites: true,
  coalesceWindowMs: 10,
});

const failedProductsCache = localspace.createInstance({
  name: getCacheInstanceNameOnLocale('failedProducts'),
  plugins: [ttlPlugin({ defaultTTL: ttlTenMinutes })], // Auto-expire after 10m,
  coalesceWrites: true,
  coalesceWindowMs: 10,
});

export function useRemoteProducts(selectProduct: (product: SearchProductItem) => void) {
  const products: ShallowRef<SearchProductItems> = shallowRef<SearchProductItems>([]);
  const { loading, error, data, execute } = useFetch<SearchProductItems>({ showLoadingBar: true });

  async function fetch(ids: number[]) {
    const url = new URL(resolveShopifyUrl('search'));
    const search = ids.map((id) => `id:${id}`).join(' OR ');
    url.searchParams.set('type', 'product');
    url.searchParams.set('view', 'cards-json');
    url.searchParams.set('q', search);
    await execute(url.toString());

    return SearchProductItems(data.value);
  }

  /**
   * Fetches products from a remote server using the Shopify Search.
   */
  async function fetchProducts(ids: number[]) {
    const parsedProducts = await fetch(ids);

    if (parsedProducts instanceof type.errors) {
      products.value = [];
    } else {
      products.value = parsedProducts;
    }
  }

  /**
   * Load products from different sources.
   *
   * - Uses the indexedDB to get products from a local DB if they are not outdated (1h lifetime)
   * - Fetches missing products using the fetch API
   * - Stores the collected products in the indexedDB
   * - Stores products that are not in the response as a filterable empty response for one hour as well.
   */
  async function loadProducts(ids: number[]): Promise<void> {
    const lookupIds = new Set<number>();
    const productsMap = new Map<number, SearchProductItem>();
    for (const id of ids) {
      const card = await productsCache.getItem<SearchProductItem>(`products:${id}`);
      if (!card) {
        const isKnownFailure = await failedProductsCache.getItem<boolean>(id);
        if (!isKnownFailure) {
          lookupIds.add(id);
        }
        continue;
      }

      productsMap.set(card.id, card);
    }

    if (lookupIds.size > 0) {
      const fetchedProducts = await fetch(Array.from(lookupIds));
      if (fetchedProducts instanceof type.errors) {
        console.warn(`products not found`, fetchedProducts.flatProblemsByPath);
        return;
      }

      for (const product of fetchedProducts) {
        await productsCache.setItem(`products:${product.id}`, product);
        productsMap.set(product.id, product);
      }
    }

    for (const productId of ids) {
      if (!productsMap.has(productId)) {
        await failedProductsCache.setItem(`failedProducts:${productId}`, true);
      }
    }

    products.value = Array.from(productsMap.values());
    const firstProduct = products.value.at(0);
    if (firstProduct) {
      selectProduct(firstProduct);
    }
  }

  return { products, loading, error, fetchProducts, loadProducts };
}

function getCacheInstanceNameOnLocale(prefix: string): string {
  const lang = document.documentElement.lang ?? 'common';
  return `${prefix}:${lang}:cache`;
}
